// @ts-nocheck
/*
 * Port of tts-plans/add.js (179 lines). BYTE-IDENTICAL.
 * Simple TTS Plan creator (plan_name / unit_balance / per_character /
 * per_variable). Unit Balance is Rupees (whole numbers only) or Paise
 * (up to 4 decimal places). Values are clamped to 0-1000.
 * Plan-name regex allows `[A-Za-z0-9 ]`.
 *
 * Note on siblings: v1 has `tts-plans/_form.js` that's not imported
 * anywhere â€” it's leftover from voice-plan and doesn't ship. Skipped
 * from this port intentionally.
 */
import React, { useMemo, useState } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall } from '@/service/httpCall';
import { API_TTS_PLANS_CREATE } from '@/service/url';
import { useNavigate } from 'react-router-dom';
import { getUser } from '@/utilities/setting';
import { getAssignedTtsPlanFloor, getTtsPlanFloorError } from '@/utilities/ttsPlanPricing';
import { ROUTES } from '@/utilities/constant';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as PageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const MAX_TTS_PRICE_DECIMALS = 4;
const TTS_UNIT_RUPEES = 'rupees';
const TTS_UNIT_PAISE = 'paise';

function sanitizeTtsPriceInput(raw: string, allowDecimals: boolean): string | null {
  const value = String(raw).replace(/[+\-eE]/g, '');
  if (value === '') return '';
  if (allowDecimals) {
    if (!/^\d*\.?\d*$/.test(value)) return null;
    const decimalParts = value.split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > MAX_TTS_PRICE_DECIMALS) {
      return null;
    }
  } else {
    if (!/^\d+$/.test(value)) return null;
  }
  if (value !== '.' && Number(value) > 1000) {
    return null;
  }
  return value;
}

function toWholeTtsPrice(value: any) {
  if (value === '' || value === '.' || value == null) return '';
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(Math.min(n, 1000));
}

function isPositiveTtsPrice(value: any, allowDecimals = true) {
  const num = Number(value);
  if (value === '' || value === '.' || !Number.isFinite(num) || num <= 0 || num > 1000) {
    return false;
  }
  if (!allowDecimals && (String(value).includes('.') || !Number.isInteger(num))) {
    return false;
  }
  return true;
}

export default function VoicePlanCreate() {
  const navigate = useNavigate();
  const ttsPlanFloor = useMemo(() => getAssignedTtsPlanFloor(getUser()), []);
  const [planName, setPlanName] = useState('');
  const [buttonDisable, setButtonDisable] = useState(false);
  const [perCharacter, setPerCharacter] = useState<any>('');
  const [perVariable, setPerVariable] = useState<any>('');
  const [unitBalance, setUnitBalance] = useState(TTS_UNIT_RUPEES);
  const allowDecimals = unitBalance === TTS_UNIT_PAISE;
  const floorError = getTtsPlanFloorError(ttsPlanFloor, {
    per_character: perCharacter,
    per_variable: perVariable,
    unit_balance: unitBalance,
  });

  function handleUnitBalanceChange(next: string) {
    setUnitBalance(next);
    if (next === TTS_UNIT_RUPEES) {
      setPerCharacter((prev: any) => toWholeTtsPrice(prev));
      setPerVariable((prev: any) => toWholeTtsPrice(prev));
    }
  }

  function validateForm() {
    if (!planName.trim() || !perCharacter || !perVariable) {
      toast.warning('All fields are required');
      return false;
    }

    if (!isPositiveTtsPrice(perCharacter, allowDecimals) || !isPositiveTtsPrice(perVariable, allowDecimals)) {
      toast.warning(
        allowDecimals
          ? 'Per Character and Per Variable must be greater than 0 and at most 1000'
          : 'Rupees cannot have decimals. Enter whole numbers for Per Character and Per Variable',
      );
      return false;
    }

    if (floorError) {
      toast.warning(floorError);
      return false;
    }

    return true;
  }

  function submitForm(e: any) {
    e.preventDefault();

    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    const params = {
      plan_name: planName,
      per_character: Number(perCharacter),
      per_variable: Number(perVariable),
      unit_balance: unitBalance,
    };

    postApiCall(API_TTS_PLANS_CREATE, params)
      .then(onVoicePlanSuccess)
      .catch(onVoicePlanError);
  }

  function onVoicePlanSuccess() {
    toast.success('TTS plan created successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.TTSPLANS.LIST}`);
    }, 1500);
  }

  function onVoicePlanError(err: any) {
    setButtonDisable(false);
    toast.error(friendlyApiError(err) || 'Failed to create TTS plan');
  }
  const planNameRegex = /^[a-zA-Z0-9 ]*$/;

  return (
    <ScrollPage>
      <PageHeader
        title="Add TTS Plan"
        description="Configure per-character pricing for text-to-speech usage."
        actions={
          <PageButton asChild variant="outline">
            <Link to={`/${ROUTES.TTSPLANS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </PageButton>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <form onSubmit={(e) => submitForm(e)}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Plan Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={planName}
                onChange={(e) => {
                  const value = e.target.value;
                  if (planNameRegex.test(value)) {
                    setPlanName(value);
                  }
                }}
                placeholder="Plan Name"
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Unit Balance <span className="text-destructive">*</span>
              </label>
              <select
                value={unitBalance}
                onChange={(e) => handleUnitBalanceChange(e.target.value)}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              >
                <option value={TTS_UNIT_RUPEES}>Rupees</option>
                <option value={TTS_UNIT_PAISE}>Paise</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Per Character <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                inputMode={allowDecimals ? 'decimal' : 'numeric'}
                value={perCharacter}
                onChange={(e) => {
                  const next = sanitizeTtsPriceInput(e.target.value, allowDecimals);
                  if (next !== null) setPerCharacter(next);
                }}
                onKeyDown={(e) => {
                  const blocked = allowDecimals
                    ? ['e', 'E', '+', '-']
                    : ['e', 'E', '+', '-', '.'];
                  if (blocked.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder={allowDecimals ? 'e.g. 0.05' : '1 – 1000'}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Per Variable <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                inputMode={allowDecimals ? 'decimal' : 'numeric'}
                value={perVariable}
                onChange={(e) => {
                  const next = sanitizeTtsPriceInput(e.target.value, allowDecimals);
                  if (next !== null) setPerVariable(next);
                }}
                onKeyDown={(e) => {
                  const blocked = allowDecimals
                    ? ['e', 'E', '+', '-']
                    : ['e', 'E', '+', '-', '.'];
                  if (blocked.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder={allowDecimals ? 'e.g. 0.05' : '1 – 1000'}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>
          </div>

          {ttsPlanFloor ? (
            <p className={`mt-3 text-xs ${floorError ? 'text-destructive' : 'text-muted-foreground'}`}>
              {floorError ||
                `Prices cannot be lower than your company's assigned plan "${ttsPlanFloor.plan_name}" (${ttsPlanFloor.per_character} / ${ttsPlanFloor.per_variable} ${ttsPlanFloor.unit_balance}).`}
            </p>
          ) : null}

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
            <PageButton asChild variant="outline">
              <Link to={`/${ROUTES.TTSPLANS.LIST}`}>Cancel</Link>
            </PageButton>
            <PageButton
              type="submit"
              disabled={
                buttonDisable ||
                !planName?.trim() ||
                !isPositiveTtsPrice(perCharacter, allowDecimals) ||
                !isPositiveTtsPrice(perVariable, allowDecimals) ||
                !!floorError
              }
              className="gap-1.5"
            >
              {buttonDisable && (
                <span
                  className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                  aria-hidden
                />
              )}
              Add TTS Plan
            </PageButton>
          </div>
        </form>
      </div>
    </ScrollPage>
  );
}
